import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';

export interface User {
  email: string;
  Role: string;
  role: string;
  id: number;
  companyID: number;
  distributorID: number | null;
  organizationID: number | null;
  clinicID: number | null;
  CanViewPHI?: boolean | number;
  canViewPHI?: boolean | number;
  Name?: string;
  Username?: string;
}

interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  refreshUser: () => void;
}

// HIPAA hardening:
// The user identity object (email, scope IDs, role) is now held only in
// React state + an auth-cookie-backed session. We no longer persist it to
// localStorage, which is plaintext on disk and survives logout. On a fresh
// load, the app re-fetches `/api/users/me` (handled by the existing axios
// 401 → /api/refresh flow) instead of restoring user from disk.
//
// Cross-tab sync uses a non-PHI ping channel: when a tab logs in or out,
// it writes a timestamp to localStorage('mn_auth_event'); other tabs see
// the storage event and call refreshUser(), which fetches /api/users/me.

const AUTH_EVENT_KEY = 'mn_auth_event';
// Legacy keys we proactively purge on first load so old PHI on disk is wiped.
const LEGACY_KEYS = ['user', 'savedAppealLetters'];

const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  refreshUser: () => {},
});

async function fetchMe(): Promise<User | null> {
  try {
    // Use the shared axios instance so a 401 transparently refreshes the
    // access token via /api/refresh before retrying.
    const res = await api.get('/api/users/me');
    const data = res.data;
    const raw = (data?.user as User) ?? (data as User) ?? null;
    if (!raw) return null;
    const canViewPHI = raw.CanViewPHI === true || raw.CanViewPHI === 1 || raw.canViewPHI === true || raw.canViewPHI === 1;
    return { ...raw, CanViewPHI: canViewPHI, canViewPHI };
  } catch {
    return null;
  }
}

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);

  // Login and logout now navigate within the SPA instead of doing a full-page
  // reload, so the React Query cache survives an identity change. Clear it
  // whenever the signed-in user changes (logout, or switching accounts in the
  // same tab) so one user can never momentarily see another user's cached PHI.
  const queryClient = useQueryClient();
  const prevUserIdRef = useRef<number | null>(null);
  useEffect(() => {
    const currentId = user?.id ?? null;
    if (prevUserIdRef.current !== null && prevUserIdRef.current !== currentId) {
      queryClient.clear();
    }
    prevUserIdRef.current = currentId;
  }, [user?.id, queryClient]);

  // One-time purge of any legacy plaintext PHI/identity in localStorage.
  useEffect(() => {
    try {
      LEGACY_KEYS.forEach((k) => localStorage.removeItem(k));
    } catch { /* ignore quota/private mode errors */ }
  }, []);

  // Initial load: ask the server who we are. The auth_token cookie is
  // httpOnly and sent automatically; no PHI ever lives on disk.
  useEffect(() => {
    let cancelled = false;
    fetchMe().then((u) => { if (!cancelled) setUserState(u); });
    return () => { cancelled = true; };
  }, []);

  const setUser = useCallback((newUser: User | null) => {
    setUserState(newUser);
    try {
      // Cross-tab signal only — no PHI in the value.
      localStorage.setItem(AUTH_EVENT_KEY, String(Date.now()));
    } catch { /* ignore */ }
  }, []);

  const refreshUser = useCallback(() => {
    fetchMe().then(setUserState);
  }, []);

  // Listen for auth-event pings from other tabs and re-fetch.
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (e.key === AUTH_EVENT_KEY) {
        refreshUser();
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [refreshUser]);

  return (
    <UserContext.Provider value={{ user, setUser, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextType => {
  return useContext(UserContext);
};

export default UserContext;
